import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import styled from 'styled-components'
import { ModalV2, Button, SegmentedControl } from '@components'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import { AI_BLUEPRINT_TYPE_OPTIONS, AIBlueprintType, BlueprintType } from '@constants'
import { useBusinessContext } from '@context'
import { createDocumentsWithAIFromAttachments } from '@query'
import toast from 'react-hot-toast'

const MAX_ATTACHMENTS = 10

interface Props {
  isVisible: boolean
  onClose: () => void
  onCreateStarted?: (payload: { jobIds: string[] }) => void
}

export const CreateDocumentWithAIModal: React.FC<Props> = ({
  isVisible,
  onClose,
  onCreateStarted
}) => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()
  const [documentType, setDocumentType] = useState<AIBlueprintType>(BlueprintType.PURCHASE)
  const [attachmentIds, setAttachmentIds] = useState<number[]>([])

  const { mutate: startCreate, isPending: isSubmitting } = useMutation({
    mutationFn: (payload: { document_type: AIBlueprintType; attachment_ids: number[] }) =>
      createDocumentsWithAIFromAttachments({ slug }, payload),
    onSuccess: data => {
      onCreateStarted?.({ jobIds: data.job_ids })
      onClose()
    },
    onError: (error: { data?: { error_code?: string; error_message?: string } }) => {
      const msg = error?.data?.error_message || t('document.createWithAI.error.analysis_failed')
      toast.error(msg)
    }
  })

  const handleAttachmentIdsChange = useCallback(
    (next: number[]) => {
      if (next.length > MAX_ATTACHMENTS) {
        toast.error(t('document.createWithAI.error.too_many_files'))
        setAttachmentIds(next.slice(0, MAX_ATTACHMENTS))
      } else {
        setAttachmentIds(next)
      }
    },
    [t]
  )

  const handleStart = () => {
    if (attachmentIds.length === 0) {
      return
    }
    startCreate({
      document_type: documentType,
      attachment_ids: attachmentIds
    })
  }

  const resetForm = () => {
    setDocumentType(BlueprintType.PURCHASE)
    setAttachmentIds([])
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (!isVisible) resetForm()
  }, [isVisible])

  const canStart = Boolean(
    attachmentIds.length > 0 && attachmentIds.length <= MAX_ATTACHMENTS && !isSubmitting
  )
  const selectedDocumentTypeLabel =
    documentType === 'PURCHASE'
      ? t('constants.documentType.purchase')
      : documentType === 'SALES'
      ? t('constants.documentType.sales')
      : null

  return (
    <ModalV2
      isVisible={isVisible}
      header={t('document.createWithAI.title')}
      handleOnClose={handleClose}
      width={600}
      height={680}
      footer={
        <FooterActions>
          <Button type="button" onClick={handleClose} disabled={isSubmitting}>
            {t('document.createWithAI.close')}
          </Button>
          <Button
            type="button"
            intent={'success'}
            onClick={handleStart}
            disabled={!canStart}
            data-test="create-document-with-ai-start"
          >
            {t('document.createWithAI.start')}
          </Button>
        </FooterActions>
      }
    >
      <ModalBody>
        <SegmentedControlLabel>
          {t('document.createWithAI.typeSelectionWarning', {
            documentType: selectedDocumentTypeLabel
          })}
        </SegmentedControlLabel>
        <SegmentedControl
          value={documentType}
          onUpdate={(value: AIBlueprintType) => setDocumentType(value)}
          options={AI_BLUEPRINT_TYPE_OPTIONS.map(({ value, labelKey }) => ({
            value,
            label: t(labelKey)
          }))}
        />

        <AttachmentSelector
          value={attachmentIds}
          onChange={handleAttachmentIdsChange}
          disabled={isSubmitting}
          appearance="dropzone"
        />
        {attachmentIds.length >= MAX_ATTACHMENTS && (
          <MaxHint>{t('document.createWithAI.error.too_many_files')}</MaxHint>
        )}
      </ModalBody>
    </ModalV2>
  )
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const MaxHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.error};
`

const SegmentedControlLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.warning};
`

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
